import { get, post, put } from "aws-amplify/api";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function ArticleCategoryList({ isAuthenticated, user }) {
  const articleCategoryIdPrefix = "article-category-list";

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);

  // --- API Functions ---
  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await get({ apiName: "colin", path: "/articlecategories" });
      const response = await result.response;
      const json = await response.body.json();
      setData(json || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setUi({ showAlert: true, errorMessage: "Failed to load categories." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchCategories();
  }, [isAuthenticated, fetchCategories]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/articlecategories/new" : `/articlecategories/${record.article_category_code}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      const response = await result.response;
      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  const handleSave = async ({ row, column, value }) => {
    const originalRecord = row.original;
    const recordToSave = {
      ...originalRecord,
      [column.id]: value
    };

    if (column.id === "article_category_code" && !originalRecord.new_record) {
      if (value !== originalRecord.article_category_code) {
        setUi({
          showAlert: true,
          errorMessage: "You cannot modify an existing article category code."
        });
        setData(old => [...old]);
        return;
      }
    }

    if (recordToSave.article_category_code.trim() === "") {
      setUi({ showAlert: true, errorMessage: "Code cannot be blank. The record has not been saved" });
      setData(old => [...old]);
      return;
    }

    if (recordToSave.description.trim() === "") {
      setUi({ showAlert: true, errorMessage: "Description cannot be blank. The record has not been saved" });
      setData(old => [...old]);
      return;
    }

    if (recordToSave.isSaving) return;

    setData(old => old.map((r, index) =>
      index === row.index ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      const response = await saveRecord(recordToSave);

      if (response?.result === false) {
        setData(old => old.map((r, index) =>
          index === row.index ? { ...r, isSaving: false } : r
        ));
        setUi({
          showAlert: true,
          errorMessage: response.error_message || "Failed to save the record."
        });
        return;
      }

      setData(old => old.map((r, index) =>
        index === row.index
          ? {
            ...r,
            [column.id]: value,
            modified: false,
            isSaving: false,
            new_record: false
          }
          : r
      ));
      setUi({ showAlert: false, errorMessage: "" });

    } catch (err) {
      console.error("Save error:", err);
      setData(old => old.map((r, index) =>
        index === row.index ? { ...r, isSaving: false } : r
      ));
      setUi({
        showAlert: true,
        errorMessage: "Network Error: Failed to save changes."
      });
    }
  };

  const addRecord = () => {
    setData(prev => [{
      article_category_code: "",
      description: "",
      created_by: user,
      new_record: true,
      modified: true
    }, ...prev]);
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("article_category_code", {
      header: "Code",
      cell: EditableCell,
      size: 150,
      meta: { maxLength: 5 },
    }),
    columnHelper.accessor("description", {
      header: "Description",
      cell: EditableCell,
      size: 350,
    })
  ], []);

  const createId = useMemo(
    () => createElementIdFactory(articleCategoryIdPrefix),
    [articleCategoryIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  return (
    <div >
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="article-category-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.articleCategory}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default ArticleCategoryList;